import React, { useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useEditMode } from '../../context/EditModeContext';
import { useContent } from '../../context/ContentContext';
import { resolveHeroSlides } from '../../data/homeHeroSlides';
import { promptImageUrl, readImageFile } from '../../utils/imageUpload';
import './editable.css';

export default function EditableHeroSlideshow({ contentKey, defaultSlides, activeIndex }) {
  const { isEditing } = useAuth();
  const { controlsVisible, activeFieldKey } = useEditMode();
  const showEditTools = isEditing && (controlsVisible || activeFieldKey === contentKey);
  const { getContent, setContentValue } = useContent();
  const fileInputRef = useRef(null);
  const replaceIndexRef = useRef(null);

  const slides = resolveHeroSlides(getContent);

  const saveSlides = (nextSlides) => {
    setContentValue(contentKey, nextSlides);
  };

  const replaceSlideAt = (index, imageSrc) => {
    const nextSlides = [...slides];
    nextSlides[index] = imageSrc;
    saveSlides(nextSlides);
  };

  const removeSlideAt = (index) => {
    if (slides.length <= 1) {
      window.alert('You need at least one hero slide.');
      return;
    }
    if (!window.confirm('Remove this slide?')) return;
    saveSlides(slides.filter((_, itemIndex) => itemIndex !== index));
  };

  const addSlide = (imageSrc) => {
    saveSlides([...slides, imageSrc]);
  };

  const applyFile = async (file, handler) => {
    try {
      const dataUrl = await readImageFile(file);
      handler(dataUrl);
    } catch (error) {
      window.alert(error.message);
    }
  };

  const openReplacePicker = (index) => {
    replaceIndexRef.current = index;
    fileInputRef.current?.click();
  };

  return (
    <div
      className={`hero-slideshow ${isEditing ? 'hero-slideshow--editing' : ''}`}
      data-content-key={contentKey}
      aria-hidden={!isEditing ? 'true' : undefined}
    >
      {slides.map((slide, index) => (
        <div
          key={`${contentKey}-${index}`}
          className={`hero-slide ${index === activeIndex ? 'active' : ''}`}
          style={{ backgroundImage: `url(${slide})` }}
        >
          {showEditTools && (
            <span className="editable-image-toolbar editable-image-toolbar--hero" role="toolbar" aria-label={`Edit slide ${index + 1}`}>
              <button type="button" className="editable-image-btn" onClick={() => openReplacePicker(index)}>
                Replace
              </button>
              <button
                type="button"
                className="editable-image-btn"
                onClick={() => {
                  const url = promptImageUrl();
                  if (url) replaceSlideAt(index, url);
                }}
              >
                URL
              </button>
              <button type="button" className="editable-image-btn editable-image-btn--danger" onClick={() => removeSlideAt(index)}>
                Remove
              </button>
            </span>
          )}
        </div>
      ))}

      {showEditTools && (
        <div className="hero-slide-add">
          <button
            type="button"
            className="editable-gallery-add-btn"
            onClick={() => {
              replaceIndexRef.current = slides.length;
              fileInputRef.current?.click();
            }}
          >
            + Add slide
          </button>
          <button
            type="button"
            className="editable-gallery-add-btn editable-gallery-add-btn--secondary"
            onClick={() => {
              const url = promptImageUrl();
              if (url) addSlide(url);
            }}
          >
            Add slide from URL
          </button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="editable-image-input"
        onChange={(e) => {
          const file = e.target.files?.[0];
          const index = replaceIndexRef.current;
          if (!file || index === null) return;

          applyFile(file, (src) => {
            if (index >= slides.length) addSlide(src);
            else replaceSlideAt(index, src);
          });

          e.target.value = '';
          replaceIndexRef.current = null;
        }}
      />
    </div>
  );
}
